import { isFocusable, isTabbable, tabbable } from 'tabbable'

import { findClosest, isDisabled } from './dom'
import { noArgCallSafely } from './fn'

export function isFocused(el: HTMLElement) {
  return document.activeElement === el
}

export function isFocusWithin(el: HTMLElement) {
  return el.contains(document.activeElement)
}

/**
 * Focus typically returns to the body when it is lost, either by blurring or
 * the currently focused element being removed.
 */
export function isDefaultElementFocused() {
  const { activeElement, body, documentElement } = document
  return activeElement == null || activeElement === body || activeElement === documentElement
}

export function findClosestFocusable(el: Element, stopAt?: Element | null) {
  return findClosest(el, isFocusable, stopAt)
}

/** EXPERIMENTAL */
export function findClosestFocusableIgnoringDisplayNone(el: Element) {
  return findClosest(el, (node) => isFocusable(node, { displayCheck: 'none' }))
}

/**
 * Check if focus is leaving the element identified by the event's `currentTarget` value.
 * This **must** be a `blur` event. Any other event type will return `false`.
 */
export function isFocusLeaving(e: React.FocusEvent | FocusEvent) {
  if (e.type !== 'blur' || !(e.currentTarget instanceof Element)) return false
  return !e.relatedTarget || !e.currentTarget.contains(e.relatedTarget as Element)
}

/**
 * A wrapper around the native `HTMLElement.focus` method that also
 * selects the contents of input elements.
 */
export function focusElement(el: HTMLElement | null | undefined) {
  if (!el) return null

  el.focus()
  // This is emulating tab behavior, which includes selecting the contents of input elements.
  // Focus by itself doesn't do this - if the focused element has a select method, call it.
  noArgCallSafely(el, 'select')

  return el
}

export function blurActiveElement() {
  noArgCallSafely(document.activeElement, 'blur')
}

/** Return an array of all tabbable elements within the given element. */
export function getTabbableElements(el: HTMLElement) {
  return tabbable(el, { getShadowRoot: true }) as HTMLElement[]
}

/**
 * Get the first and last tabbable elements inside of `el`. Returns a `[null, null]` tuple if
 * there are no tabbable elements, and `[first, last]` if there are. First and last are the same
 * element if there is only one tabbable element.
 */
export function getFirstAndLastTabbable(el: HTMLElement) {
  const walker = createTabbableWalker(el)

  const firstEl = walker.nextNode() as HTMLElement | null

  walker.currentNode = el
  const lastEl = last(walker)

  return [firstEl, lastEl] as const
}

interface CreateFocusManagerOpts {
  /**
   * Whether this focus manager should move focus to elements.
   * - `tabbable`: is this element reachable via <kbd>Tab</kbd>
   * - `focusable`: all tabbable elements, plus anything with [tabindex] (including unchecked checkboxes/radios)
   * - `focusableNonDisabled`: focusable, but also skip over :disabled and [aria-disabled] elements as well (which is not always desirable with focusable!)
   * - `firstFocusableMenuGroup`: (menu only) jump to the first item within a non-disabled menu group
   */
  criteria?: 'tabbable' | 'focusable' | 'focusableNonDisabled' | 'firstFocusableMenuGroup'
}

/** Create an object with methods for managing focus within a given element. */
export function createFocusManager(
  ref: React.RefObject<HTMLElement | null>,
  { criteria = 'tabbable' }: CreateFocusManagerOpts = {},
) {
  const createWalker = {
    tabbable: createTabbableWalker,
    focusable: createFocusableWalker,
    focusableNonDisabled: createFocusableNonDisabledWalker,
    firstFocusableMenuGroup: createFirstFocusableMenuGroupWalker,
  }[criteria]

  return {
    /**
     * Focus the tabbable element after the activeElement. If the activeElement is not a
     * descendant of the root element, the first tabbable element will be focused.
     *
     * @param wrap If true, the focus will wrap around to the first tabbable element if the last
     *   tabbable element is currently focused.
     *
     * @returns The element that was focused, or null if no element was focused.
     */
    focusNext({ wrap = false }: { wrap?: boolean } = {}) {
      const root = ref.current
      if (!root) return null

      const node = getActiveElement(root)
      const walker = createWalker(root, node)

      let nextNode = walker.nextNode() as HTMLElement | null
      if (!nextNode && wrap) {
        walker.currentNode = root
        nextNode = walker.nextNode() as HTMLElement | null
      }

      return focusElement(nextNode)
    },

    /**
     * Focus the tabbable element before the activeElement. If the activeElement is not a
     * descendant of the root element, the last tabbable element will be focused.
     *
     * @param wrap If true, the focus will wrap around to the last tabbable element if the first
     *   tabbable element is currently focused.
     *
     * @returns The element that was focused, or null if no element was focused.
     */
    focusPrevious({ wrap }: { wrap?: boolean } = {}) {
      const root = ref.current
      if (!root) return null

      const node = getActiveElement(root)
      const walker = createWalker(root, node)

      if (walker.currentNode === root) {
        return focusElement(last(walker))
      }

      let previousNode = walker.previousNode() as HTMLElement | null
      if ((!previousNode || previousNode === root) && wrap) {
        walker.currentNode = root
        previousNode = last(walker)
      }

      if (previousNode === root) {
        // NOTE(yan):
        // based on the spec for `previousNode` https://dom.spec.whatwg.org/#dom-treewalker-previousnode
        //   it is possible for `previousNode` to return to `root` if `root` passes the criteria.
        // for more details see https://github.com/figma/figma/pull/494850
        return null
      }

      return focusElement(previousNode)
    },

    /** Focus the first tabbable element within the root. */
    focusFirst() {
      const root = ref.current
      if (!root) return null

      const walker = createWalker(root)
      return focusElement(walker.nextNode() as HTMLElement | null)
    },

    /** Focus the last tabbable element within the root. */
    focusLast() {
      const root = ref.current
      if (!root) return null

      const walker = createWalker(root)
      return focusElement(last(walker))
    },
  }
}

/**
 * Create a [tree walker] that filters for tabbable elements.
 *
 * @param root The element to contain the walker within.
 * @param start Optional starting element for the walker. If omitted, or if the element is
 *   not a descendant of `root`, the walker will start at the `root`.
 *
 * [tree walker]: https://developer.mozilla.org/en-US/docs/Web/API/TreeWalker
 */
const createTabbableWalker = makeTreeWalker(isTabbable)

/**
 * Create a [tree walker] that filters for focusable elements.
 *
 * @param root The element to contain the walker within.
 * @param start Optional starting element for the walker. If omitted, or if the element is
 *   not a descendant of `root`, the walker will start at the `root`.
 *
 * [tree walker]: https://developer.mozilla.org/en-US/docs/Web/API/TreeWalker
 */
const createFocusableWalker = makeTreeWalker(isFocusable)

/**
 * Create a [tree walker] that filters for focusable AND non-disabled elements
 */
const createFocusableNonDisabledWalker = makeTreeWalker(isFocusableNonDisabled)

/**
 * Create a tree walker that finds the first item in a menu group. Used only for
 * jumping between menu sections.
 */
const createFirstFocusableMenuGroupWalker = makeTreeWalker(isFirstFocusableMenuGroup)

/**
 * Walks a tree walker to the last node. The built in `lastChild` method does not traverse into
 * descendants unless the currentNode is already the last node, which is why we need this function.
 */
function last(walker: TreeWalker) {
  while (walker.lastChild()) {
    /* keep calling lastChild until it returns null */
  }
  const current = walker.currentNode as HTMLElement
  // The `.lastChild()` call will return null if we're already at the last child, but
  // it does not change the `currentNode` in that case. If there are no matching nodes
  // within the root, the walker will still be at the root, and we'll incorrectly return
  // the root as the last node, so return null instead.
  return current === walker.root ? null : current
}

function getActiveElement(root: HTMLElement) {
  const rootNode = root.getRootNode()
  return 'activeElement' in rootNode
    ? (rootNode.activeElement as HTMLElement | null)
    : (root.ownerDocument.activeElement as HTMLElement | null)
}

function isFocusableNonDisabled(...params: Parameters<typeof isFocusable>): boolean {
  return isFocusable(...params) && !isDisabled(params[0])
}

function isFirstFocusableMenuGroup(...params: Parameters<typeof isFocusable>): boolean {
  const [el] = params

  if (!isFocusable(...params) || isDisabled(el)) return false

  // happy path: item is focusable, not disabled, and within a group
  // note: closest() starts with current element, we want the parent
  if (el.parentElement?.closest('[role="group"]')) {
    // either this is the first child of the group, OR the first focusable, non-disabled child of the group
    return (
      !el.previousElementSibling ||
      el.parentElement?.querySelector('[tabindex]:not([aria-disabled]):not(:disabled)') === el
    )
  }

  // As a fallback, we’ll allow ungrouped items in the top level, provided
  // they’re focusable & non-disabled
  return true
}

function makeTreeWalker(predicate: (el: Element) => boolean) {
  return (root: HTMLElement, start?: HTMLElement | null) => {
    const walker = document.createTreeWalker(root, 1 /* NodeFilter.SHOW_ELEMENT */, (el) =>
      predicate(el as Element) ? /* NodeFilter.FILTER_ACCEPT */ 1 : /* NodeFilter.FILTER_SKIP */ 3,
    )

    // calling `contains` with `null` will always return false, and
    // `contains` returning `true` implies that `start` was not null
    if (root.contains(start as HTMLElement)) {
      walker.currentNode = start!
    }

    return walker
  }
}
